import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import React from 'react';
import { toast } from 'react-toastify';
import { z } from 'zod';

import { handleUnexpectedError } from '@/app/errors';
import { useCurrentUser } from '@/app/hooks/use-current-user';
import { useAppForm } from '@/app/lib/tanstack-form';
import { confirmSmsVerificationCode, requestSmsVerificationCode } from '@/app/network/firefly-authenticated-api';
import { Button, ButtonGroup } from '@/common/components/button';
import { Description, Field, FieldGroup, FieldInfo, Fieldset, Label } from '@/common/components/fieldset';
import { FormInput } from '@/common/components/form-input';
import { Heading } from '@/common/components/heading';
import { Text } from '@/common/components/text';
import { isNullOrWhitespace } from '@/common/string';
import { SettingsSidebar } from '@/settings/components/settings-sidebar';
import { ContentSection } from '@/shared/components/content-section';
import { Toast } from '@/shared/components/toast';
import { phoneNumberMask, smsVerificationCodeMask } from '@/shared/masks';
import { cleanPhoneNumber } from '@/shared/phone-numbers';
import { getCurrentUserQueryOptions } from '@/shared/user/queries';

export const Route = createFileRoute('/settings/user-profile/update-sms')({
  beforeLoad: async ctx => ({
    user: await ctx.context.ensureUser()
  }),
  head: () => ({
    meta: [
      { title: 'Update SMS Phone Number | myTalquin' }
    ]
  }),
  component: UpdateSmsPage
});

function UpdateSmsPage() {
  const { id: userId, sms_phone_number } = useCurrentUser();
  const [codeSent, setCodeSent] = React.useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const requestSmsVerificationCodeMutation = useMutation({
    mutationFn: async (value: { phone: string }) => requestSmsVerificationCode(userId, cleanPhoneNumber(value.phone)),
    onSuccess: () => setCodeSent(true),
    onError: error => handleUnexpectedError(error)
  });

  const confirmSmsVerificationCodeMutation = useMutation({
    mutationFn: async (value: { phone: string; code: string }) => confirmSmsVerificationCode(userId, cleanPhoneNumber(value.phone), parseInt(value.code)),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: getCurrentUserQueryOptions()['queryKey'] });
      toast.success(Toast, { data: { title: 'SMS phone number updated successfully.' } });
      await navigate({ to: '/settings/user-profile' });
    },
    onError: error => handleUnexpectedError(error)
  });

  const form = useAppForm({
    defaultValues: {
      phone: '',
      code: ''
    },
    validators: {
      onBlur: z.object({
        phone: z.string()
          .min(1, 'Phone number is required.')
          .superRefine((value, ctx) => {
            if (isNullOrWhitespace(value)) return;

            if (cleanPhoneNumber(value) === sms_phone_number?.replace('+1', '')) {
              ctx.addIssue({
                code: 'custom',
                message: 'Phone number is already set.'
              });
              return;
            }
          }),
        code: z.string().min(1, 'Verification code is required.')
      })
    },
    onSubmit: async ({ value }) => {
      await confirmSmsVerificationCodeMutation.mutateAsync({ ...value });
    }
  });

  const handleSendCodeClick = () => {
    form.validateField('phone', 'blur');
    form.fieldInfo.phone.instance?.handleBlur();

    if (form.getFieldMeta('phone')?.isValid) {
      requestSmsVerificationCodeMutation.mutate({ phone: form.state.values.phone });
    }
  };

  return (
    <main className="flex flex-col md:flex-row h-full w-full p-0">
      <SettingsSidebar active="user-profile"/>
      <ContentSection bg="white">
        <Heading>Update SMS Phone Number</Heading>

        <form onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          void form.handleSubmit(event);
        }}
        >
          <Fieldset>
            <Text>Verify a mobile number so you can receive text message notifications.</Text>
            <FieldGroup>
              <form.AppField name="phone">
                {field => (
                  <Field>
                    <Label>Mobile Phone</Label>
                    <div data-slot="control" className="flex gap-4">
                      <FormInput type="tel" mask={phoneNumberMask} className="flex-1" />
                      <Button onClick={handleSendCodeClick} disabled={requestSmsVerificationCodeMutation.isPending}>
                        {requestSmsVerificationCodeMutation.isPending ? 'Sending...' : 'Send Code'}
                      </Button>
                    </div>
                    <FieldInfo field={field} />
                    <Description>We will text a verification code to this number. Standard text message fees may apply.</Description>
                  </Field>
                )}
              </form.AppField>

              {codeSent && (
                <form.AppField name="code">
                  {field => (
                    <Field>
                      <Label>Verification Code</Label>
                      <FormInput type="text" inputMode="numeric" mask={smsVerificationCodeMask} placeholder="Enter 6-digit code" />
                      <FieldInfo field={field} />
                      <Description>Enter the verification code sent to your phone.</Description>
                    </Field>
                  )}
                </form.AppField>
              )}
            </FieldGroup>
          </Fieldset>

          <ButtonGroup>
            <Button plain to="/settings/user-profile">Cancel</Button>
            <form.Subscribe selector={state => state.isSubmitting}>
              {isSubmitting => (
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Updating...' : 'Update'}
                </Button>
              )}
            </form.Subscribe>
          </ButtonGroup>
        </form>

      </ContentSection>
    </main>
  );
}
